import { AlertCircleFilled, CheckCircleFilled, Close, LoadingBlue, SearchBold } from '@tencent/odc-iconpack';
import { Popover } from '@tencent/one-design-next';
import classNames from 'classnames';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';

import './odcInput.scss';

export enum InputStatusMap {
  error = 'error',
  success = 'success',
  loading = 'loading',
}

export interface InputRef {
  focus: () => void;
}

type OdcInputProps = {
  className?: string;
  search?: boolean;
  clear?: boolean;
  onClear?: () => void;
  onChange?: (event: any, value: string) => any;
  value: string;
  status?: `${InputStatusMap}` | '';
  helpMsg?: string;
  style?: React.CSSProperties;
  onBlur?: () => void;
  onFocus?: () => void;
  autoFocus?: boolean;
  placeholder?: string;
  disabled?: boolean;
  type?: 'text' | 'number' | 'digit' | 'url' | 'button' | 'checkbox' | 'color' | 'date' | 'datetime-local' | 'email';
  onClick?: (event: React.MouseEvent) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onPressEnter?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  id?: string;
  name?: string;
  autoComplete?: string;
  maxLength?: number;
  readOnly?: boolean;
  hasErrorIcon?: boolean;
  errorIconTips?: string;
};

const OdcInput = forwardRef<InputRef, OdcInputProps>(function OdcInput(props, ref) {
  const {
    className,
    search,
    clear,
    onClear,
    value,
    status,
    helpMsg,
    style,
    onChange,
    onKeyDown,
    onPressEnter,
    disabled,
    readOnly,
    autoFocus,
    hasErrorIcon = false,
    errorIconTips,
    ...rest
  } = props;

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange?.(e, e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      onPressEnter?.(e);
    }
    onKeyDown?.(e);
  };

  const handleClear = () => {
    onClear?.();
    inputRef.current?.focus();
  };

  const showClearIcon = clear && !disabled && !readOnly && value && value.length > 0;

  return (
    <div
      className={classNames(
        'odc-input',
        {
          'odc-input-search': search,
          'odc-input-clear': clear,
          'odc-input-error': status === InputStatusMap.error,
          'odc-input-success': status === InputStatusMap.success,
          'odc-input-loading': status === InputStatusMap.loading,
        },
        className,
      )}
      style={style}
    >
      <div className={classNames('odc-input-wrapper', { disabled })}>
        {search && (
          <span className="odc-input-prefix">
            <div className="search-icon">
              <SearchBold className="text-grey-8" />
            </div>
          </span>
        )}

        <input
          ref={inputRef}
          className="odc-input-inner"
          value={value}
          disabled={disabled}
          readOnly={readOnly}
          autoFocus={autoFocus}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          {...rest}
        />

        <span className="odc-input-suffix">
          <div className="flex items-center justify-center">
            {showClearIcon && (
              <div className="cancel-block" onClick={handleClear}>
                <Close className="text-grey-8" />
              </div>
            )}
            {status === InputStatusMap.loading && (
              <div className="loading-icon">
                <LoadingBlue className="animate-spin" />
              </div>
            )}
            {status === InputStatusMap.success && (
              <div className="success-icon">
                <CheckCircleFilled className="text-success" />
              </div>
            )}
            {status === InputStatusMap.error && hasErrorIcon && (
              <div className="error-icon">
                {errorIconTips ? (<Popover popup={errorIconTips}
                  placement="bottom"
                  arrowed={false}
                ><AlertCircleFilled className="text-error" /></Popover>) : <AlertCircleFilled className="text-error" />}
              </div>
            )}
          </div>
        </span>
      </div>

      {helpMsg && <div className="help-msg">{helpMsg}</div>}
    </div>
  );
});

export default OdcInput;